import { checkoutService } from 'core/services/checkout/checkout.service';
import type { TSliceExtraReducer } from 'core/store';
import { authGetCustomerSelector } from 'core/store/auth/selectors';
import { createAppAsyncThunk } from 'core/store/store.utils';
import { handleServiceException } from 'shared/utils/errors.utils';
import {
  getCustomerIdFromCheckout,
  getDefaultHybridPaymentMethodId,
  getInitialCheckoutParams,
  getInitialCheckoutValues,
} from '../checkout-state.utils';
import { checkoutInitialState, resetCheckoutLoading, SLICE_NAMESPACE } from '../constants';
import type { ICheckoutState } from '../types';

export const refreshCheckoutDetailAction = createAppAsyncThunk(
  `${SLICE_NAMESPACE}/refreshCheckout`,
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const { checkout, auth } = state;
      const customer = authGetCustomerSelector(state);
      const customerId = getCustomerIdFromCheckout(checkout);
      const { source } = customer;

      const params = getInitialCheckoutParams({
        checkoutDetail: checkout.detail,
        checkoutPaymentMethod: checkout.paymentMethod,
        checkoutAddress: checkout.address,
        customerSource: source,
      });

      const detail = await checkoutService.getCheckoutDetail(customerId, source, params);
      const initialValues = getInitialCheckoutValues(detail, customer);
      const paymentMethodInitial = getDefaultHybridPaymentMethodId(auth, detail);

      return {
        detail,
        initialValues: { ...initialValues, paymentMethod: paymentMethodInitial },
      };
    } catch (error) {
      return rejectWithValue(handleServiceException(error));
    }
  },
);

export const refreshCheckoutReducer: TSliceExtraReducer<ICheckoutState> = builder => {
  builder
    .addCase(refreshCheckoutDetailAction.fulfilled, (state, { payload }) => {
      const { detail, initialValues } = payload;
      state.loading = resetCheckoutLoading;
      state.detail = detail;
      state.shipping.deliveryDates = initialValues.deliveryDates;
      state.paymentMethod = initialValues.paymentMethod;
    })
    .addCase(refreshCheckoutDetailAction.rejected, (state, { payload }) => {
      state.loading = resetCheckoutLoading;
      const { error: initialError } = checkoutInitialState;

      if (payload)
        state.error = {
          ...initialError,
          global: payload,
        };
    });

  return builder;
};
